// src/utils/productAdapter.js

export const MAX_STOREFRONT_ITEM_QUANTITY = 999;

export function extractProductsFromResponse(data) {
  const candidates = [
    data?.items,
    data?.products,
    data?.data?.items,
    data?.data?.products,
    data?.data,
    data,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
  }

  return [];
}

export function extractProductFromResponse(data) {
  return (
    data?.item ||
    data?.product ||
    data?.data?.item ||
    data?.data?.product ||
    data?.data ||
    data ||
    null
  );
}

export function getProductId(product) {
  return product?._id || product?.id || product?.slug || "";
}

export function getProductName(product) {
  return product?.nombre || product?.name || "";
}

export function getProductDescription(product) {
  return product?.descripcion || product?.description || "";
}

export function getProductCategory(product) {
  return product?.categoria || product?.category || "";
}

export function getProductSubcategory(product) {
  return product?.subcategoria || product?.subcategory || product?.subCategory || "";
}

export function getGastronomicMenu(product) {
  const source = product?.gastronomicMenu || product?.menuGastronomico || {};
  return {
    enabled: source?.enabled === true,
    available: source?.available !== false && source?.disponible !== false,
    shortDescription: String(source?.shortDescription || source?.descripcionCorta || ""),
    dietaryNotes: String(source?.dietaryNotes || source?.observacionesAlimentarias || ""),
    section: String(source?.section || source?.seccion || ""),
    sectionOrder: Number(source?.sectionOrder ?? source?.ordenSeccion ?? 0) || 0,
    sortOrder: Number(source?.sortOrder ?? source?.orden ?? 0) || 0,
    featured: source?.featured === true || source?.destacado === true,
  };
}

export function isInGastronomicMenu(product) {
  return getGastronomicMenu(product).enabled;
}

export function getProductCurrency(product, variant = null, fallback = "ARS") {
  return String(
    variant?.currency ||
      variant?.pricing?.currency ||
      product?.priceCurrency ||
      product?.precioMoneda ||
      product?.currency ||
      product?.pricing?.currency ||
      fallback
  ).toUpperCase();
}

export function getProductBasePrice(product, variant = null) {
  const value = variant
    ? variant?.basePrice ?? variant?.baseUnitPrice ?? variant?.price ?? variant?.precio
    : product?.basePrice ?? product?.precioBase ?? product?.price ?? product?.precio;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function getProductPrice(product, variant = null) {
  const variantValue = variant?.price ?? variant?.precio;
  const value =
    variantValue === null || variantValue === undefined || variantValue === ""
      ? product?.precio ?? product?.price ?? 0
      : variantValue;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function getQuantityPriceTiers(product, variant = null) {
  const source =
    variant?.quantityPriceTiers ||
    variant?.quantityPricing?.tiers ||
    product?.quantityPriceTiers ||
    product?.quantityPricing?.tiers ||
    product?.quantityDiscounts ||
    product?.descuentosPorCantidad ||
    [];
  return (Array.isArray(source) ? source : [])
    .map((tier) => ({
      minQuantity: Number(tier?.minQuantity ?? tier?.quantity),
      unitPrice: Number(tier?.unitPrice ?? tier?.price),
      baseUnitPrice: Number(tier?.baseUnitPrice ?? tier?.configuredUnitPrice ?? tier?.unitPrice),
    }))
    .filter(
      (tier) =>
        Number.isInteger(tier.minQuantity) &&
        tier.minQuantity >= 2 &&
        Number.isFinite(tier.unitPrice) &&
        tier.unitPrice >= 0,
    )
    .sort((left, right) => left.minQuantity - right.minQuantity);
}

export function getAppliedQuantityTier(product, variant = null, quantity = 1) {
  const normalizedQuantity = Math.max(1, Number.parseInt(quantity, 10) || 1);
  let selected = null;
  for (const tier of getQuantityPriceTiers(product, variant)) {
    if (tier.minQuantity <= normalizedQuantity) selected = tier;
    else break;
  }
  return selected;
}

export function getProductPriceForQuantity(product, variant = null, quantity = 1) {
  return getAppliedQuantityTier(product, variant, quantity)?.unitPrice ?? getProductPrice(product, variant);
}

/** Precio antiguo de referencia: nunca se utiliza para cobrar. */
export function getProductCompareAtPrice(product, variant = null, quantity = 1) {
  if (getPriceType(product) === "quote") return null;
  const source = product?.compareAtPrice ?? product?.precioTachadoWeb;
  if (source === null || source === undefined || source === "") return null;
  const compared = Number(source);
  if (!Number.isFinite(compared) || compared <= 0) return null;
  // Sin variante elegida, la referencia sólo se exhibe si supera todo el rango.
  const current = variant
    ? getProductPriceForQuantity(product, variant, quantity)
    : hasProductVariants(product)
      ? Number(product?.priceRange?.max ?? getProductPrice(product))
      : getProductPriceForQuantity(product, null, quantity);
  return Number.isFinite(current) && compared > current ? compared : null;
}

export function getProductStock(product, variant = null) {
  const value = variant ? variant?.stock ?? 0 : product?.stock ?? 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function getItemType(product) {
  const value = String(product?.itemType || product?.type || product?.tipo || "product")
    .trim()
    .toLowerCase();
  return ["service", "servicio"].includes(value) ? "service" : "product";
}

export function isService(product) {
  return getItemType(product) === "service";
}

export function getPriceType(product) {
  const value = String(product?.priceType || product?.precioTipo || "fixed")
    .trim()
    .toLowerCase();
  if (["from", "desde"].includes(value)) return "from";
  if (["quote", "consultar"].includes(value)) return "quote";
  return "fixed";
}

export function getProductUnit(product) {
  return product?.unit || product?.unidad || "";
}

export function getProductCode(product) {
  return product?.code || product?.codigo || product?.sku || "";
}

export function getServiceDuration(product) {
  const value = product?.durationMinutes ?? product?.duracionMinutos ?? 0;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function getServiceModality(product) {
  return product?.modality || product?.modalidad || "";
}

export function requiresBooking(product) {
  return Boolean(product?.requiresBooking ?? product?.requiereReserva);
}

export function getCalendlyUrl(product) {
  const raw = String(product?.calendlyUrl || product?.urlCalendly || "").trim();
  if (!raw) return "";
  try {
    const url = new URL(raw);
    const host = url.hostname.toLowerCase();
    if (
      url.protocol !== "https:" ||
      !(host === "calendly.com" || host.endsWith(".calendly.com")) ||
      !url.pathname ||
      url.pathname === "/"
    ) {
      return "";
    }
    return url.toString();
  } catch {
    return "";
  }
}

export function getBookingProvider(product) {
  if (!requiresBooking(product)) return "none";
  const provider = String(product?.bookingProvider || product?.proveedorReserva || "")
    .trim()
    .toLowerCase();
  if (provider === "calendly" && getCalendlyUrl(product)) return "calendly";
  return "webprofit";
}

export function getCalendlyDisplayMode(product) {
  return String(product?.calendlyDisplayMode || product?.modoCalendly || "")
    .trim()
    .toLowerCase() === "inline"
    ? "inline"
    : "popup";
}

export function getProductAttributes(product) {
  const attributes = product?.attributes;
  if (!attributes || typeof attributes !== "object" || Array.isArray(attributes)) return [];
  return Object.entries(attributes)
    .map(([label, value]) => ({ label: String(label), value: String(value ?? "") }))
    .filter((item) => item.label && item.value);
}

export function getVariantAttributes(product) {
  const source = product?.variantAttributes || product?.variantOptions || [];
  return (Array.isArray(source) ? source : [])
    .map((axis, index) => ({
      key: String(axis?.key || axis?.id || `opcion_${index + 1}`),
      label: String(axis?.label || axis?.name || `Opción ${index + 1}`),
      values: Array.isArray(axis?.values || axis?.options)
        ? (axis.values || axis.options).map(String).filter(Boolean)
        : [],
      displayType: axis?.displayType || axis?.type || "button",
    }))
    .filter((axis) => axis.key && axis.label);
}

export function getProductVariants(product) {
  const source = product?.variants || product?.variations || [];
  return (Array.isArray(source) ? source : [])
    .map((variant) => ({
      ...variant,
      id: String(variant?.id || variant?._id || ""),
      _id: String(variant?._id || variant?.id || ""),
      attributes: variant?.attributes || variant?.combination || {},
      price:
        variant?.price === null || variant?.price === undefined
          ? null
          : Number(variant.price),
      basePrice: Number(variant?.basePrice ?? variant?.baseUnitPrice ?? variant?.price ?? 0),
      currency: getProductCurrency(product, variant),
      pricing: variant?.pricing || product?.pricing || null,
      quantityPricing: variant?.quantityPricing || product?.quantityPricing || null,
      quantityPriceTiers: getQuantityPriceTiers(product, variant),
      stock: Number(variant?.stock || 0),
      imageUrl: variant?.imageUrl || variant?.image || "",
      active: variant?.active !== false,
    }))
    .filter((variant) => variant.id && variant.active !== false);
}

export function hasProductVariants(product) {
  return Boolean(
    product?.hasVariants === true ||
      (getVariantAttributes(product).length && getProductVariants(product).length),
  );
}

export function getVariantLabel(product, variant) {
  if (!variant) return "";
  return getVariantAttributes(product)
    .map((axis) => variant?.attributes?.[axis.key])
    .filter(Boolean)
    .join(" / ");
}

export function findVariantBySelection(product, selection = {}) {
  const axes = getVariantAttributes(product);
  if (!axes.length) return null;
  return (getProductVariants(product) || []).find((variant) =>
    axes.every(
      (axis) =>
        String(variant?.attributes?.[axis.key] || "") ===
        String(selection?.[axis.key] || ""),
    ),
  ) || null;
}

export function getAvailableVariantValues(product, axisKey, selection = {}) {
  const axes = getVariantAttributes(product);
  const variants = getProductVariants(product);
  const previousAxes = axes.slice(0, Math.max(0, axes.findIndex((axis) => axis.key === axisKey)));
  const matchesPrevious = variants.filter((variant) =>
    previousAxes.every((axis) => {
      const selected = selection?.[axis.key];
      return !selected || String(variant?.attributes?.[axis.key] || "") === String(selected);
    }),
  );
  return new Set(
    matchesPrevious
      .filter((variant) => Number(variant.stock || 0) > 0)
      .map((variant) => String(variant?.attributes?.[axisKey] || ""))
      .filter(Boolean),
  );
}

export function isProductActive(product) {
  const value = product?.activo ?? product?.active;
  return value !== false;
}

export function productTracksStock(product = {}) {
  // HOTFIX 2026-10: vender sin controlar stock no convierte la publicación
  // en una consulta. Las variantes, precio y checkout siguen funcionando;
  // solamente se elimina la restricción por existencia física.
  const candidates = [
    product?.operational?.stockTracked,
    product?.capabilities?.stockTracked,
    product?.stockTracked,
    product?.tracksStock,
  ];
  for (const value of candidates) {
    if (typeof value === "boolean") return value;
  }
  return !isService(product);
}

export function canBuyProduct(product, variant = null) {
  if (isService(product) || getPriceType(product) !== "fixed") return false;
  if (hasProductVariants(product) && !variant) return false;
  if (!productTracksStock(product)) return true;
  return getProductStock(product, variant) > 0;
}

export function formatDuration(minutes) {
  const value = Number(minutes || 0);
  if (!value) return "";
  if (value < 60) return `${value} min`;
  const hours = Math.floor(value / 60);
  const rest = value % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

export function getPriceLabel(product, formatter, variant = null, quantity = 1) {
  const type = getPriceType(product);
  if (type === "quote") return "Consultar precio";

  if (!variant && hasProductVariants(product)) {
    const min = Number(product?.priceRange?.min);
    const max = Number(product?.priceRange?.max);
    if (Number.isFinite(min) && Number.isFinite(max)) {
      const formattedMin = formatter(min);
      if (type === "from" || min !== max) {
        return min === max ? `Desde ${formattedMin}` : `${formattedMin} – ${formatter(max)}`;
      }
      return formattedMin;
    }
  }

  const formatted = formatter(getProductPriceForQuantity(product, variant, quantity));
  return type === "from" ? `Desde ${formatted}` : formatted;
}

export function normalizeProduct(product) {
  if (!product || typeof product !== "object") return product;

  const itemType = getItemType(product);
  const priceType = getPriceType(product);
  const variantAttributes = getVariantAttributes(product);
  const variants = getProductVariants(product);
  return {
    ...product,
    _id: product?._id || product?.id,
    id: product?.id || product?._id,
    nombre: getProductName(product),
    name: getProductName(product),
    descripcion: getProductDescription(product),
    description: getProductDescription(product),
    categoria: getProductCategory(product),
    category: getProductCategory(product),
    subcategoria: getProductSubcategory(product),
    subcategory: getProductSubcategory(product),
    precio: getProductPrice(product),
    price: getProductPrice(product),
    precioBase: getProductBasePrice(product),
    compareAtPrice: product?.compareAtPrice ?? product?.precioTachadoWeb ?? null,
    basePrice: getProductBasePrice(product),
    currency: getProductCurrency(product),
    pricing: product?.pricing || null,
    quantityPricing: product?.quantityPricing || null,
    quantityPriceTiers: getQuantityPriceTiers(product),
    stock: getProductStock(product),
    tipo: itemType === "service" ? "servicio" : "producto",
    itemType,
    type: itemType,
    precioTipo: priceType === "from" ? "desde" : priceType === "quote" ? "consultar" : "fijo",
    priceType,
    activo: isProductActive(product),
    active: isProductActive(product),
    hasVariants: Boolean(product?.hasVariants || (variantAttributes.length && variants.length)),
    variantAttributes,
    variantOptions: variantAttributes,
    variants,
    variations: variants,
  };
}

export function normalizeProducts(list) {
  return (Array.isArray(list) ? list : []).map(normalizeProduct).filter(Boolean);
}
